/* The sign-in page — the one page outside the portal.
 *
 * The design project has no sign-in, so this is built in the house style:
 * the rail's dark ground, one blueprint card in the middle carrying the
 * portal's own eyebrow and title. There is no "forgot password": an admin
 * resets it from the Data loads page. */

import { useState, type FormEvent } from 'react'
import { Blueprint } from '../components/ui'
import { auth, type User } from '../lib/auth'

export default function SignIn({ onSignedIn }: { onSignedIn: (u: User) => void }) {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (busy) return
    const name = username.trim()
    if (!name || !password) { setError('Enter your username and password.'); return }
    setBusy(true)
    setError(null)
    try {
      onSignedIn(await auth.login(name, password))
    } catch (err: any) {
      setError(err?.message ?? String(err))
      setBusy(false)
    }
    // On success the page unmounts; nothing left to reset.
  }

  return (
    <div style={{
      minHeight: '100vh', display: 'grid', placeItems: 'center', padding: 24,
      background: 'var(--rail-bg)',
    }}>
      <Blueprint style={{
        width: 'min(100%, 380px)', padding: '28px 30px 26px',
        background: 'var(--color-bg)', color: 'var(--color-text)',
      }}>
        <div className="eyebrow">Mohans Sri Chand Tex</div>
        <h1 style={{ fontSize: 32, lineHeight: 1, margin: '6px 0 22px' }}>Mill dashboards</h1>

        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span className="axis-title">Username</span>
            <input className="input" type="text" name="username" autoFocus
              autoComplete="username" autoCapitalize="none" spellCheck={false}
              value={username} disabled={busy}
              onChange={(e) => setUsername(e.target.value)} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span className="axis-title">Password</span>
            <input className="input" type="password" name="password"
              autoComplete="current-password"
              value={password} disabled={busy}
              onChange={(e) => setPassword(e.target.value)} />
          </label>

          {error ? (
            <div role="alert" style={{ fontSize: 13, color: 'var(--color-bad)' }}>{error}</div>
          ) : null}

          <button className="btn btn-primary" type="submit" disabled={busy}
            style={{ marginTop: 4 }}>
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <div style={{ fontSize: 12, color: 'var(--color-neutral-600)', marginTop: 18 }}>
          No account, or a forgotten password? Ask an admin.
        </div>
      </Blueprint>
    </div>
  )
}
